import axios from "axios";
import axiosInstance from "./axiosInstance";

// Token Usage API
export const saveTokenUsage = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/token-usage-service/tokenusage`,
      payload
    );
    return response;
  } catch (error) {
    console.error('Token usage API error:', error);
    // Don't throw error to avoid breaking chat flow
    return { data: { success: false, error: error.message } };
  }
};

// Update human-in-loop references for a chat
export const updateHumanInLoopRefs = async (chatId, agentId) => {
  try {
    const response = await axiosInstance.patch(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/tokenusage/human-refs`,
      { chatId, agentId }
    );
    return response;
  } catch (error) {
    console.error('Update human-in-loop refs error:', error);
    // Don't throw error to avoid breaking agent registration flow
    return { data: { success: false, error: error.message } };
  }
};

// Get user's groups from user-service
export const getUserGroups = async (userId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_USER_SERVICE_ENDPOINT}/user/${userId}/groups`
    );
    return response;
  } catch (error) {
    console.error('Get user groups error:', error);
    // Don't throw error to avoid breaking chat flow
    return { data: { success: false, data: [] } };
  }
};

// Get groups for multiple users in batch from user-service
export const getUserGroupsBatch = async (userIds) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_USER_SERVICE_ENDPOINT}/users/groups/batch`,
      { userIds }
    );
    return response;
  } catch (error) {
    console.error('Get user groups batch error:', error);
    return { data: { success: false, data: {} } };
  }
};

export const getTokenUsageStats = async (chatId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/tokenusage?chatId=${chatId}`
    );
    return response;
  } catch (error) {
    console.error('Get token usage stats error:', error);
    throw error;
  }
};

// Token Usage Analytics API
export const getTokenUsageSummary = async (startDate, endDate, orgKey, datasetId, userIds) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (datasetId) params.append('datasetId', datasetId);
    
    // Handle multiple user IDs
    if (userIds && Array.isArray(userIds) && userIds.length > 0) {
      if (userIds.length === 1) {
        params.append('userId', userIds[0]);
      } else {
        params.append('userIds', userIds.join(','));
      }
    }

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/tokenusage/summary?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Get token usage summary error:', error);
    throw error;
  }
};

export const getTokenUsageDaily = async (startDate, endDate, orgKey, datasetId, userIds) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (datasetId) params.append('datasetId', datasetId);
    
    // Handle multiple user IDs
    if (userIds && Array.isArray(userIds) && userIds.length > 0) {
      if (userIds.length === 1) {
        params.append('userId', userIds[0]);
      } else {
        params.append('userIds', userIds.join(','));
      }
    }

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/tokenusage/daily?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Get token usage daily error:', error);
    throw error;
  }
};

export const getTokenUsageByModel = async (startDate, endDate, orgKey, datasetId, userIds) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (datasetId) params.append('datasetId', datasetId);
    
    // Handle multiple user IDs
    if (userIds && Array.isArray(userIds) && userIds.length > 0) {
      if (userIds.length === 1) {
        params.append('userId', userIds[0]);
      } else {
        params.append('userIds', userIds.join(','));
      }
    }

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/tokenusage/by-model?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Get token usage by model error:', error);
    throw error;
  }
};

export const getTokenUsageByUser = async (startDate, endDate, orgKey, datasetId, userIds = null, limit = 10) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (datasetId) params.append('datasetId', datasetId);
    if (limit) params.append('limit', limit.toString());
    
    // Handle multiple user IDs for filtering top users by selected group
    if (userIds && Array.isArray(userIds) && userIds.length > 0) {
      if (userIds.length === 1) {
        params.append('userId', userIds[0]);
      } else {
        params.append('userIds', userIds.join(','));
      }
    }

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/tokenusage/by-user?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Get token usage by user error:', error);
    throw error;
  }
};

export const getTokenUsageByOrg = async (startDate, endDate, orgKey, datasetId, userId) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (orgKey) params.append('orgKey', orgKey);
    if (datasetId) params.append('datasetId', datasetId);
    if (userId) params.append('userId', userId);

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/tokenusage/by-org?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Get token usage by organization error:', error);
    throw error;
  }
};

// Session Usage Analytics API
export const getSessionUsageSummary = async (startDate, endDate, orgKey, datasetId, userIds) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (datasetId) params.append('datasetId', datasetId);
    
    // Handle multiple user IDs
    if (userIds && Array.isArray(userIds) && userIds.length > 0) {
      if (userIds.length === 1) {
        params.append('userId', userIds[0]);
      } else {
        params.append('userIds', userIds.join(','));
      }
    }

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/sessionusage/summary?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Get session usage summary error:', error);
    throw error;
  }
};

export const getSessionUsageData = async (
  startDate, endDate, orgKey, datasetId, userIds, 
  page = 1, pageSize = 50, search = null,
  sortField = null, sortOrder = null,
  filterField = null, filterOperator = null, filterValue = null
) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (datasetId) params.append('datasetId', datasetId);
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('pageSize', pageSize.toString());
    if (search) params.append('search', search);
    
    // Sort parameters
    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
    
    // Filter parameters
    if (filterField) params.append('filterField', filterField);
    if (filterOperator) params.append('filterOperator', filterOperator);
    if (filterValue) params.append('filterValue', filterValue);
    
    // Handle multiple user IDs for filtering
    if (userIds && Array.isArray(userIds) && userIds.length > 0) {
      if (userIds.length === 1) {
        params.append('userId', userIds[0]);
      } else {
        params.append('userIds', userIds.join(','));
      }
    }

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/sessionusage/data?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Get session usage data error:', error);
    throw error;
  }
};

export const getCompanies = async (datasetId) => {
  try {
    const params = new URLSearchParams();
    if (datasetId) params.append('datasetId', datasetId);

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/sessionusage/companies?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Get companies error:', error);
    throw error;
  }
};

export const getSessionUsers = async (startDate, endDate, datasetId) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (datasetId) params.append('datasetId', datasetId);

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TOKEN_USAGE_API_ENDPOINT}/sessionusage/users?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Get session users error:', error);
    throw error;
  }
};

export const deletePrompt = async (datasetId) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PROMPT_API_ENDPOINT}/${datasetId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const addDocument = async (payload, datasetId) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${datasetId}/document`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const addEngagementAPI = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ENGAGEMENT_API_ENDPOINT}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getEngagementAPI = async (
  userId = null,
  datasetId = null,
  startDate = null,
  endDate = null
) => {
  try {
    let url = `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ENGAGEMENT_API_ENDPOINT}`;

    // Construct query parameters based on the provided filters
    const params = new URLSearchParams();
    if (userId) {
      params.append("userId", userId);
    }
    if (datasetId) {
      params.append("datasetId", datasetId);
    }
    if (startDate) {
      params.append("startDate", startDate.toISOString());
    }
    if (endDate) {
      params.append("endDate", endDate.toISOString());
    }
    // Send user's local IANA timezone so backend aggregates dates correctly
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (userTimezone) {
      params.append("timezone", userTimezone);
    }

    // Append query parameters to the URL if any
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getDocumentDetails = async (datasetId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${datasetId}/document`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteDocumentFile = async (payload) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${payload.datasetId}/document/${payload.id}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const downloadDocumentFile = async (datasetId, docId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${datasetId}/document/${docId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// The storage account backing dataset documents has public network access
// disabled, so the SAS "downloadUrl" above is unreachable directly from a
// browser (403 AuthorizationFailure). This streams the bytes through an
// authenticated backend route instead, same pattern as getImageBlob below.
export const getDocumentContentBlob = async (datasetId, docId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${datasetId}/document/${docId}/content`,
      { responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Same endpoint as getDocumentContentBlob above, but as a {url, httpHeaders}
// descriptor instead of a pre-fetched Blob -- react-pdf/pdf.js takes this
// shape directly and does its own (range-request-capable) fetching against
// it, so a PDF preview can render page 1 before the whole file arrives
// instead of waiting on a full axios download first. This request never goes
// through axiosInstance (pdf.js does its own fetch), so the auth headers are
// built here rather than touching the shared axios interceptor used by every
// other call in the app.
export const getDocumentPreviewSource = (datasetId, docId) => {
  const token = localStorage.getItem("token");
  const httpHeaders = {
    "Ocp-Apim-Subscription-Key": process.env.REACT_APP_SUBSCRIPTION_KEY,
  };
  if (token) {
    httpHeaders["x-user-token"] = `Bearer ${token}`;
    httpHeaders["Authorization"] = `Bearer ${token}`;
  }
  return {
    url: `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${datasetId}/document/${docId}/content`,
    httpHeaders,
  };
};

// Same fix, for the "source document" preview/download under chat answers
// (ChatBot.js / ExternalUser.js), which look documents up by file name.
export const getFileContentBlob = async (datasetId, fileName) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${datasetId}/file/content?name=${encodeURIComponent(fileName)}`,
      { responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addDataSets = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getDataSet = async () => {
  try {
    // 🌍 Build the full API URL using env variables
    const url = `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}`;
   

    const response = await axiosInstance.get(url, {
      headers: {
        "Content-Type": "application/json",
      },
    });

   
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteDataSet = async (payload) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${payload}`
    );
    z;
    return response;
  } catch (error) {
    throw error;
  }
};

export const allDocumentUsers = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ADMIN_USER_API_ENDPOINT}?role=User`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const allDatasetUsers = async (datasetId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${datasetId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const addDatasetUsers = async (payload, datasetId) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${datasetId}/user`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteDocUser = async (datasetId, userID) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${datasetId}/user/${userID}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getChatHistoryData = async (page = 1, pageSize = 20, search = "") => {
  try {
    let url = `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_CHAT_API_ENDPOINT}?page=${page}&pageSize=${pageSize}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAllChatHistoryData = async () => {
  const PAGE_SIZE = 1000;
  const firstPage = await getChatHistoryData(1, PAGE_SIZE);
  const pagination = firstPage.data?.pagination;
  let allRecords = firstPage.data?.data || [];

  if (pagination && pagination.totalPages > 1) {
    const requests = [];
    for (let page = 2; page <= pagination.totalPages; page++) {
      requests.push(getChatHistoryData(page, PAGE_SIZE));
    }
    const responses = await Promise.all(requests);
    for (const res of responses) {
      const pageData = res.data?.data || [];
      allRecords = allRecords.concat(pageData);
    }
  }

  return allRecords;
};

// Get all unique users who have chat history (for Admin/OrgAdmin/HumanAgent)
export const getChatUsers = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_CHAT_API_ENDPOINT}/users`
    );
    return response;
  } catch (error) {
    console.error('Get chat users error:', error);
    throw error;
  }
};

export const getRadioDataSets = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}?withDocuments=1`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteChatHistory = async (payload) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_CHAT_API_ENDPOINT}/${payload}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const editChatHistory = async (payload, id) => {
  try {
    const response = await axiosInstance.put(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_CHAT_API_ENDPOINT}/${id}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};
export const getChatHistoryConversationData = async (chatBotQueryParam, retryCount = 0) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_CHAT_API_ENDPOINT}/${chatBotQueryParam}/conversation`
    );

    // Check if response data is an empty array and retry once
    if (Array.isArray(response?.data) && response.data.length === 0 && retryCount === 0) {
      // Retry once after a short delay (300ms)
      await new Promise(resolve => setTimeout(resolve, 500));
      return getChatHistoryConversationData(chatBotQueryParam, retryCount + 1);
    }

    return response;
  } catch (error) {
    // No retry on errors - only retry when response is empty array
    throw error;
  }
};

export const getChatHistoryByFiltering = async (queryString, page = 1, pageSize = 20, search = "") => {
  try {
    const separator = queryString.includes('?') ? '&' : '?';
    let url = `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_CHAT_API_ENDPOINT}${queryString}${separator}page=${page}&pageSize=${pageSize}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getQnaChatbotData = async (payload, datasetId, chatID) => {
  try {
    // Get agenticQnAEnabled from the payload (default to false if not present)
    const agenticQnAEnabled =
      payload?.agenticQnAEnabled === true ||
      payload?.agenticQnAEnabled === "true";

    // Use agent endpoint if agenticQnAEnabled is true, else use default
    const endpoint = agenticQnAEnabled
      ? process.env.REACT_APP_AGENT_QUERY_API_ENDPOINT
      : process.env.REACT_APP_QUERY_API_ENDPOINT;

    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${endpoint}/${datasetId}/query/${chatID}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const addChatHistoryData = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_CHAT_API_ENDPOINT}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const saveChatHistoryConversation = async (
  payload,
  chatBotQueryParam
) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_CHAT_API_ENDPOINT}/${chatBotQueryParam}/conversation`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// apiEndpointEnvVar: pass process.env.REACT_APP_QUERY_API_ENDPOINT for images
// that came from a live qna-service answer, or process.env.REACT_APP_CHAT_API_ENDPOINT
// for images that came from chat-service history. relativeImagePath is the
// "images?path=<encoded blob path>" string returned in chatMeta.imageBlobURLs.
export const getImageBlob = async (relativeImagePath, apiEndpointEnvVar) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${apiEndpointEnvVar}/${relativeImagePath}`,
      { responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getApprovedDataSet = async (payload) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}`,
      { params: payload }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const datasetRequestAccess = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/user/request`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getOthersDataSetList = async (payload) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}`,
      { params: payload }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getDocumentSummarizationData = async (datasetId, documentName) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${
        process.env.REACT_APP_SUMMARY_API_ENDPOINT
      }/document/summary/${datasetId}?file=${encodeURIComponent(documentName)}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const downloadPdfUrlChat = async (datasetId, documentName) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${
        process.env.REACT_APP_DATASET_API_ENDPOINT
      }/${datasetId}/file?name=${encodeURIComponent(documentName)}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAllUsersList = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ADMIN_USER_API_ENDPOINT}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const addUserDataB2c = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ADMIN_USER_API_ENDPOINT}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteB2cUser = async (userId) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ADMIN_USER_API_ENDPOINT}/${userId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAllDatasetRequestList = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/user/mapping?accessType=RequestPending`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAllDatasetUsersRequestList = async (userIdParam) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/user/mapping?accessType=RequestPending&userId=${userIdParam}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const approveDatasetRequestAccess = async (userId, datasetIds) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/request/approval/user/${userId}/type/Approved`,
      datasetIds
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const denyDatasetRequestAccess = async (userId, datasetIds) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/request/approval/user/${userId}/type/Denied`,
      datasetIds
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const chatFeedbackService = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_FEEDBACK_API_ENDPOINT}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const trainDocumentRequest = async (payload, id, datasetId) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_TRAINING_API_ENDPOINT}/${datasetId}/${id}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getFeedbackSugestion = async (payload) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_FEEDBACK_API_ENDPOINT}/suggestion`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getChatLikesCount = async (coversationId = "") => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_FEEDBACK_API_ENDPOINT}/chat_conversation?count=true&conversation=${coversationId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getChatLikesStatus = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_FEEDBACK_API_ENDPOINT}/user/like/status`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const UnfilteredFeedback = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_FEEDBACK_API_ENDPOINT}/like/status`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const UpdateUserDataB2c = async (payload, userId) => {
  try {
    const response = await axiosInstance.put(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ADMIN_USER_API_ENDPOINT}/${userId}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const summarizationPdfUrl = async (fileName, datasetId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${
        process.env.REACT_APP_DATASET_API_ENDPOINT
      }/${datasetId}/file?name=${encodeURIComponent(fileName)}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};




export const editDatasetApi = async (payload, id, isExternalFlag = false) => {
  try {
    
    let url = `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}/${id}`;
    if (isExternalFlag) {
      url += "?isExternalDataset=true";
    }

    
    let token = localStorage.getItem("token") || "";
    if (token && !token.startsWith("Bearer ")) {
      token = `Bearer ${token}`;
    }

    

    
    if (token) {
      axiosInstance.defaults.headers.Authorization = token;
      axiosInstance.defaults.headers["x-user-token"] = token;
    }

    
    const response = await axiosInstance.put(url, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    
    return response;
  } catch (error) {
    throw error;
  }
};
export const fetchOrganisations = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ORAGANIZATION_API_ENDPOINT}/orgs`
    );
    return response.data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const addOrganization = async (organizationData) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ORAGANIZATION_API_ENDPOINT}/org`,
      organizationData
    );
    return { data: response.data, status: response.status };
  } catch (error) {
    throw new Error(error.message);
  }
};

export const fetchOrganizationDetailsAPI = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ORAGANIZATION_API_ENDPOINT}/orgdetails`
    );
    return response.data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const updateOrganization = async (organizationData) => {
  try {
    const response = await axiosInstance.put(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ORAGANIZATION_API_ENDPOINT}/org`,
      organizationData
    );
    return { data: response.data, status: response.status };
  } catch (error) {
    throw new Error(error.message);
  }
};

export const checkAdminEmailAPI = async (email) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_EMAIL_API_ENDPOINT}?email=${email}`
    );
    return response;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const createPromptAPI = async (payload, datasetId) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PROMPT_API_ENDPOINT}/${datasetId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDataSetParameters = async (datasetId, history = false) => {
  try {
    const url = history
      ? `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PROMPT_API_ENDPOINT}/${datasetId}?history=true`
      : `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PROMPT_API_ENDPOINT}/${datasetId}`;
    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getUserData = async (userId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ADMIN_USER_API_ENDPOINT}/${userId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const updatePromptAPI = async (payload, datasetId) => {
  try {
    const response = await axiosInstance.put(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PROMPT_API_ENDPOINT}/${datasetId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Fetch tools from MCP server
export const discoverMcpTools = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PROMPT_API_ENDPOINT}/mcp/tools`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete MCP tool from database
export const deleteMcpToolAPI = async (datasetId, toolId) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PROMPT_API_ENDPOINT}/${datasetId}/mcp/tools/${toolId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addStarRatingAPI = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_STAR_RATING_API_ENDPOINT}/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error in addRating:", error);
    throw error;
  }
};

export const fetchStarRatingAPI = async (chatId) => {
  try {
    const params = "?chatId=" + chatId;
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_STAR_RATING_API_ENDPOINT}/${params}`
    );
    return { data: response.data, status: response.status };
  } catch (error) {
    throw error;
  }
};

export const fetchAllStarRatings = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_STAR_RATING_API_ENDPOINT}`
    );
    return { data: response.data, status: response.status };
  } catch (error) {
    throw error;
  }
};

export const updateStarRatingAPI = async (payload) => {
  try {
    const response = await axiosInstance.put(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_STAR_RATING_API_ENDPOINT}/${payload._id}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const SendStarRatingWakeUpCall = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_STAR_RATING_WAKEUP_API_ENDPOINT}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Centralized function to wake up all backend services in parallel
 * Add new wake-up endpoints to the services array as needed
 */
export const wakeUpServices = async () => {
  const services = [
    { name: 'dataset-service', endpoint: '/dataset-service/wake-up' },
    { name: 'org-service', endpoint: '/org-service/wake-up' },
    { name: 'user-service', endpoint: '/user-service/wake-up' },
    { name: 'feedback-service', endpoint: '/feedback-service/wake-up' },
    { name: 'qna-service', endpoint: '/qna-service/wake-up' },
    { name: 'prompt-service', endpoint: '/prompt-service/wake-up' },
    { name: 'document-parameter-service', endpoint: '/document-parameter-service/wake-up' },
    { name: 'document-process-service', endpoint: '/docprocess-service/wake-up' },
    { name: 'analytics-service', endpoint: '/analytics-service/wake-up' },
    { name: 'chat-service', endpoint: '/chat-service/wake-up' }
  ];

  // Execute all wake-up calls in parallel
  const wakeUpPromises = services.map(async (service) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_API_URL}${service.endpoint}`
      );
      console.log(`${service.name} wake-up call successful`);
      return { service: service.name, success: true, response };
    } catch (error) {
      // Silent failure - wake-up calls should not block the login process
      console.warn(`${service.name} wake-up call failed:`, error.message);
      return { service: service.name, success: false, error };
    }
  });

  // Wait for all wake-up calls to complete
  const results = await Promise.allSettled(wakeUpPromises);
  return results;
};
export const createDocSettingsAPI = async (payload, datasetId) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PARAMETER_API_ENDPOINT}/${datasetId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchDocSettingsAPI = async (datasetId, history = false) => {
  try {
    const url = history
      ? `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PARAMETER_API_ENDPOINT}/${datasetId}?history=true`
      : `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PARAMETER_API_ENDPOINT}/${datasetId}`;
    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateDocSettingsAPI = async (payload, datasetId) => {
  try {
    const response = await axiosInstance.put(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PARAMETER_API_ENDPOINT}/${datasetId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteParameter = async (datasetId) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_PARAMETER_API_ENDPOINT}/${datasetId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const saveServiceNowIntegrationAPI = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/integration-service/service-agent/servicenow`,

      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getServiceNowIntegrationAPI = async (datasetId) => {
  const url = `${process.env.REACT_APP_BASE_API_URL}/integration-service/service-agent/servicenow?datasetId=${datasetId}`;
  try {
    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

// apis/index.js

export const deleteServiceNowIntegrationAPI = async (datasetId) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/integration-service/service-agent/servicenow?datasetId=${datasetId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const saveOpenAIKeyAPI = async (payload) => {
  try {
    // The endpoint URL becomes:
    // `${REACT_APP_BASE_API_URL}/service-agent/openai`
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/integration-service/openai`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// API to get OpenAI Key
export const getOpenAIKeyAPI = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/integration-service/openai`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// API to delete OpenAI Key
export const deleteOpenAIKeyAPI = async () => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/integration-service/openai`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const sendEmailSupportAPI = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/ticket-creation-service/email-support`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteEmailSupportAPI = async (toEmail, datasetId) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/ticket-creation-service/email-support`,
      { data: { toEmail, datasetId } }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getEmailSupportAPI = async (datasetId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/ticket-creation-service/email-support?datasetId=${datasetId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const registerHumanAgent = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/human-agent-service/agent`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const validateHumanAgent = async (agentId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/human-agent-service/validate?agentId=${agentId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateHumanAgentStatus = async (agentId, payload) => {
  try {
    const response = await axiosInstance.put(
      `${process.env.REACT_APP_BASE_API_URL}/human-agent-service/agent?agentId=${agentId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getHumanAgentStatus = async (agentId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/human-agent-service/agent?agentId=${agentId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateHumanAgentQna = async (agentId, type, payload) => {
  try {
    const url = `${process.env.REACT_APP_BASE_API_URL}/human-agent-service/qna?type=${type}&agentId=${agentId}`;
    const response = await axiosInstance.put(url, payload);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getHumanAgentQna = async (agentId, type) => {
  try {
    const url = `${process.env.REACT_APP_BASE_API_URL}/human-agent-service/qna?type=${type}&agentId=${agentId}`;
    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const saveHumanAgentEmail = async (payload) => {
  try {
    const url = `${process.env.REACT_APP_BASE_API_URL}/human-agent-service/configureAgentEmail`;
    const response = await axiosInstance.post(url, payload);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getHumanAgentEmail = async (datasetId) => {
  try {
    const url = `${process.env.REACT_APP_BASE_API_URL}/human-agent-service/configureAgentEmail?datasetId=${datasetId}`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteHumanAgentEmail = async (datasetId) => {
  try {
    const url = `${process.env.REACT_APP_BASE_API_URL}/human-agent-service/configureAgentEmail?datasetId=${datasetId}`;
    const response = await axiosInstance.delete(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const fetchAssistantParametersAPI = async (datasetId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ASSISTANT_SETTINGS_API_ENDPOINT}/${datasetId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const saveAssistantParametersAPI = async (datasetId, payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ASSISTANT_SETTINGS_API_ENDPOINT}/${datasetId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateAssistantParametersAPI = async (datasetId, payload) => {
  try {
    const response = await axiosInstance.put(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ASSISTANT_SETTINGS_API_ENDPOINT}/${datasetId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteAssistantSettingsAPI = async (datasetId) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ASSISTANT_SETTINGS_API_ENDPOINT}/${datasetId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const fetchCombinedDataAPI = async (userId, chatMetaId) => {
  try {
    const url = `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_MEMORY_API_ENDPOINT}?userId=${userId.userId}&chatMetaId=${userId.chatMetaId}`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Groups API Functions
export const getGroupsData = async () => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_USER_SERVICE_ENDPOINT}/groups`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const createGroupData = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_USER_SERVICE_ENDPOINT}/groups`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateGroupData = async (groupId, payload) => {
  try {
    const response = await axiosInstance.put(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_USER_SERVICE_ENDPOINT}/groups/${groupId}`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteGroupData = async (groupId) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_USER_SERVICE_ENDPOINT}/groups/${groupId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getGroupUsersData = async (groupId) => {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_USER_SERVICE_ENDPOINT}/groups/${groupId}/users`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const addUsersToGroupData = async (groupId, payload) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_USER_SERVICE_ENDPOINT}/groups/${groupId}/users`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const removeUserFromGroupData = async (groupId, userId) => {
  try {
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_USER_SERVICE_ENDPOINT}/groups/${groupId}/users/${userId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};
export const validateExternalUUID = async (uuid) => {
  try {
    const url = `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_CUSTOM_AUTH_API_ENDPOINT}/corrigo/validate/${uuid}`;
   

    const response = await axiosInstance.get(url);
   
    return response.data;
  } catch (error) {
    console.error("[ERROR] validateExternalUUID failed:", error.response?.data || error.message);
    throw error;
  }
};


/**
 * 🔹 Fetch external datasets
 */
export const getExternalDatasetAPI = async (token) => {
  try {
    const url = `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_DATASET_API_ENDPOINT}?isExternalDataset=true`;
    

    // Optionally override token in axiosInstance
    if (token) {
      const finalToken = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      axiosInstance.defaults.headers.Authorization = finalToken;
      axiosInstance.defaults.headers["x-user-token"] = finalToken;
    }

    const response = await axiosInstance.get(url);
    
    return response;
  } catch (error) {
    console.error("[ERROR] getExternalDatasetAPI failed:", error.response?.data || error.message);
    throw error;
  }
};

